import pipe from 'lodash/fp/pipe';
import VMasker from 'vanilla-masker';

export const removeMask = text => {
    if (typeof text === 'string') {
        return text.replace(/[^\da-z]/gi, '');
    }
    return text;
};

export const removeMaskAndLetters = text => {
    if (typeof text === 'string') {
        return text.replace(/[^\d]/gi, '');
    }
    return text;
};

// Telefone internacional em E.164: preserva um unico "+" inicial e descarta todo o
// resto que nao for digito. removeMaskAndLetters nao serve aqui porque apaga o "+",
// que em E.164 faz parte do numero. Usar como `parse` do campo quando isForeigner.
export const removeMaskE164 = text => {
    if (typeof text !== 'string') {
        return text;
    }

    const digits = text.replace(/[^\d]/g, '');

    return text.trimStart().startsWith('+') ? `+${digits}` : digits;
};

// CNPJ pode ser alfanumérico (Serpro, a partir de 2026): mantém dígitos e letras,
// remove pontuação e padroniza em maiúsculas. Usar como `parse` de campos de CNPJ.
export const removeMaskCNPJ = text => (
    typeof text === 'string' ? removeMask(text).toUpperCase() : text
);

export const maskCep = text => (
    typeof text === 'string' ? VMasker.toPattern(text, '99999-999') : text
);

export const maskCPF = text => (
    typeof text === 'string' ? VMasker.toPattern(text, '999.999.999-99') : ''
);

export const maskDate = text => (
    typeof text === 'string' ? VMasker.toPattern(text, '99/99/9999') : ''
);

export const maskCreditCardDueDate = text => {
    return (
        typeof text === 'string' ? VMasker.toPattern(text, '99/9999') : ''
    );
};

// CNPJ alfanumérico: 12 posições base alfanuméricas (S) + 2 dígitos verificadores (9).
// O padrão aceita tanto o CNPJ numérico tradicional quanto o novo formato alfanumérico.
export const maskCNPJ = text => (
    typeof text === 'string' ? VMasker.toPattern(text.toUpperCase(), 'SS.SSS.SSS/SSSS-99') : ''
);

export const maskTime = text => (
    typeof text === 'string' ? VMasker.toPattern(text, '99:99') : ''
);

export const maskTelefone = text => (
    typeof text === 'string'
        ? text.replace(/[^\d]/g, '').length > 10
            ? VMasker.toPattern(text, '(99) 9 9999-9999')
            : VMasker.toPattern(text, '(99) 9999-9999')
        : ''
);

export const maskInteger = text => {
    if (typeof text === 'string') {
        return text.replace(/[^\d]/gim, '');
    }
    return text;
};

export const maskCreditCardNumber = text => (
    typeof text === 'string' ? (text.length < 18
        ? VMasker.toPattern(text, '9999 999999 99999')
        : VMasker.toPattern(text, '9999 9999 9999 9999')) : ''
);

export const createDecimalFormatter = ({
    precision = 2,
    separator = ',',
    delimiter = '.',
    delimiterCase = 3,
    suffix = '',
    prefix = '',
    keepLeadingZeros = false,
}) => {
    const removeLeadingZeros = value => (keepLeadingZeros ? value : (value || '').replace(/^0+/, ''));

    const addLeadingZeros = value => (value || '').padStart(precision + 1, '0');

    const precisionRegex = new RegExp(`(\\d*?)(\\d{${precision}})$`);
    const separatorPattern = `$1${separator}$2`;
    const delimiterRegex = new RegExp(
        `(\\d+)(\\d{3}[${delimiter}${separator}\\d]*)$`,
    );
    const delimiterPattern = `$1${delimiter}$2`;

    const applyMask = (value = '') => {
        const counter = (value.length - (precision + delimiterCase)) / delimiterCase;
        let masked = precision
            ? value.replace(precisionRegex, separatorPattern)
            : value;
        for (let i = 0; i < counter; i += 1) {
            masked = masked.replace(delimiterRegex, delimiterPattern);
        }
        return masked;
    };

    const sanitizeValue = value => {
        switch (typeof value) {
            case 'string':
                return value;
            case 'number':
                return value.toFixed(precision);
            default:
                return '';
        }
    };

    const addPrefixSuffix = value => `${prefix}${value}${suffix}`;

    return pipe(
        sanitizeValue,
        removeMask,
        removeLeadingZeros,
        addLeadingZeros,
        applyMask,
        addPrefixSuffix,
    );
};

export const maskDecimal = createDecimalFormatter({
    precision: 2,
    separator: ',',
});

export const maskDecimalPrecisionThree = createDecimalFormatter({
    precision: 3,
    separator: ',',
});

export const maskProjectExternalCode = text => (
    typeof text === 'string'
        ? text.replace(/[^A-Za-z]/g, '').toUpperCase()
        : ''
);

const maskNumberWithDigit = createDecimalFormatter({
    precision: 1,
    separator: '-',
    keepLeadingZeros: true,
});

export const maskWithVerificationDigit = value => {
    const stripped = removeMask(value);
    return stripped && stripped.length >= 2
        ? maskNumberWithDigit(stripped)
        : stripped;
};

export const maskMoney = createDecimalFormatter({
    prefix: 'R$ ',
});

export const maskCpfCnpj = text => (
    typeof text === 'string'
        ? removeMask(text).length > 11
            ? maskCNPJ(text)
            : maskCPF(text)
        : text
);
